import React, {useState} from 'react';
import {Box, Typography, Grid, Tabs, Tab} from '@mui/material';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import PeopleIcon from '@material-ui/icons/People';
import StorageIcon from '@material-ui/icons/Storage';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import SchoolIcon from '@material-ui/icons/School';
import AccountBalanceIcon from '@material-ui/icons/AccountBalance';
import BusinessIcon from '@material-ui/icons/Business';
import GavelIcon from '@material-ui/icons/Gavel';
import NationalDashboardShell from './NationalDashboard.comp';
import {useDashboard} from '../../../context/DashboardContext';
import {TIER_COLOR} from '../constants';

import {READINESS_BAND_COLORS} from '../../../utils/constants';
const NAVY   = '#052F44';
const ORANGE = '#FF6900';
const GREEN  = '#1B6B55';
const BLUE   = '#3B6FA0';

/* ── Band palettes ─────────────────────────────────────────────── */
const READINESS_COLORS = READINESS_BAND_COLORS;   // the shared palette — every dashboard's Low is the same orange
const RISK_COLORS      = ['#4A9278', '#EFA020', '#E8730C', '#E04535'];   // Monitor · Watch · Elevated · Priority Concern
const CONTEXT_COLORS   = ['#1B6B55', '#4A9278', '#EFA020', '#E04535'];
const CONFIDENCE_COLORS = ['#1B6B55', '#EFA020', '#E8730C', '#9CA3AF'];  // High · Medium · Low · Very Low

/* ── Reusable pieces ───────────────────────────────────────────── */
export const SectionHeader = ({title, subtitle}) => (
    <Box sx={{mb: 3}}>
        <Typography sx={{fontSize: '23px', fontWeight: 800, color: NAVY, lineHeight: 1.25}}>
            {title}
        </Typography>
        {subtitle && (
            <Typography sx={{fontSize: '14px', fontStyle: 'italic', color: '#6B7280', mt: 0.75, lineHeight: 1.6}}>
                {subtitle}
            </Typography>
        )}
    </Box>
);

// `text` may be a single string (one flowing sentence) or an array of
// distinct items — arrays render one per line instead of running together.
export const NoteBanner = ({label, text}) => (
    <Box sx={{
        mt: 4, p: {xs: 2, md: 2.5}, borderRadius: '10px',
        backgroundColor: 'var(--brand-surface-muted, #F3F4F6)', border: '1px solid var(--brand-border, #E5E7EB)',
        borderLeft: `4px solid ${NAVY}`,
    }}>
        {label && <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mb: 0.5}}>{label}</Typography>}
        {Array.isArray(text) ? (
            text.map((line, i) => (
                <Typography key={i} sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{line}</Typography>
            ))
        ) : (
            <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{text}</Typography>
        )}
    </Box>
);

const Chip = ({text, color}) => (
    <Box sx={{
        backgroundColor: color, color: '#ffffff', fontSize: '12px', fontWeight: 700,
        px: 1.5, py: 0.5, borderRadius: '6px', whiteSpace: 'nowrap',
    }}>
        {text}
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   Tabbed page shell — used by every Overview sub-page
   ═════════════════════════════════════════════════════════════════ */
export const TabbedOverviewPage = ({tabs, skipNoDataGate = true}) => {
    const [activeTab, setActiveTab] = useState(0);
    const current = tabs[activeTab] || tabs[0];

    const tabsElement = tabs.length > 1 ? (
        <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            TabIndicatorProps={{style: {backgroundColor: ORANGE}}}
            sx={{
                borderBottom: '1px solid #E5E7EB',
                mb: 1,
                '& .MuiTab-root': {
                    textTransform: 'none', fontWeight: 600, fontSize: '14px', color: '#6A7282', px: 2,
                    whiteSpace: 'nowrap', minHeight: 48, maxWidth: 'none',
                },
                '& .Mui-selected': {color: `${ORANGE} !important`},
            }}
        >
            {tabs.map((t) => <Tab key={t.label} label={t.label}/>)}
        </Tabs>
    ) : null;

    return (
        <NationalDashboardShell skipNoDataGate={skipNoDataGate} tabs={tabsElement}>
            <Box sx={{mt: tabs.length > 1 ? 3 : 0}}>
                {current.content}
            </Box>
        </NationalDashboardShell>
    );
};

/* ══════════════════════════════════════════════════════════════════
   About COS Readiness Solution — the 5-step baseline→action→verified-
   impact journey. Same step content as every other dashboard's own
   version (word-for-word — this is the same solution being described).
   Static, no backend section — this page didn't exist in National
   Dashboard's nav before.
   ═════════════════════════════════════════════════════════════════ */
const READINESS_JOURNEY_STEPS = [
    {num: '1', title: 'COS Baseline', body: 'Measure your students’ online safety baseline through the COS assessment, covering online safety readiness, risk experiences, and context and support systems.'},
    {num: '2', title: 'Gap Diagnosis', body: 'Turn the results into priorities, so the weakest skills and items become a focused shortlist of where students need help most.'},
    {num: '3', title: 'Targeted Learning', body: 'Connect each priority to a concrete plan, pairing skill gaps with learning programs and broader needs with school or home support.'},
    {num: '4', title: 'Learning Impact', body: 'Track students’ real-time progress toward closing the priority gaps using pre and post evidence and activity evidence.'},
    {num: '5', title: 'COS Reassessment*', body: 'Repeat the COS assessment next cycle to verify which priority gaps have closed, so early progress becomes confirmed COS movement.'},
];

const JourneyStepCard = ({s, i, total}) => (
    <Box sx={{
        position: 'relative', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
    }}>
        <Box sx={{
            width: 34, height: 34, borderRadius: '50%', backgroundColor: GREEN,
            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
        }}>
            <Typography sx={{fontSize: '16px', fontWeight: 800, color: '#fff'}}>{s.num}</Typography>
        </Box>
        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5}}>{s.title}</Typography>
        <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{s.body}</Typography>
        {i < total - 1 && (
            <Typography sx={{
                display: {xs: 'none', md: 'block'}, position: 'absolute', right: -14, top: '50%',
                transform: 'translateY(-50%)', color: '#CBD5E1', fontSize: '22px', fontWeight: 800, zIndex: 1,
            }}>
                ›
            </Typography>
        )}
    </Box>
);

export const ReadinessSolutionOverviewSection = () => (
    <Box>
        <SectionHeader title="Child Online Safety (COS) Readiness Solution" subtitle="From Baseline to Action to Verified Impact"/>

        <Grid container spacing={2.5} alignItems="stretch">
            {READINESS_JOURNEY_STEPS.map((s, i) => (
                <Grid item xs={12} sm={6} md={2.4} key={s.num}>
                    <JourneyStepCard s={s} i={i} total={READINESS_JOURNEY_STEPS.length}/>
                </Grid>
            ))}
        </Grid>

        <NoteBanner text="* COS Reassessment is available upon request in the next COS cycle."/>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   Slide 2 — The Child Online Safety (COS) Assessment
   ═════════════════════════════════════════════════════════════════ */
const SUITE_STEPS = [
    {num: '1', title: 'Children',      icon: PersonOutlineIcon,  items: ['Child online safety readiness', 'Cyber risk experiences', 'Support and help-seeking environment']},
    {num: '2', title: 'Parents',       icon: PeopleIcon,         items: ['Digital parenting readiness', 'Home access and safety practices', 'Support network']},
    {num: '3', title: 'Teachers',      icon: SchoolIcon,         items: ['Risk Identification', 'Prevention and response', 'Classroom Child online safety education']},
    {num: '4', title: 'School leaders', icon: AccountBalanceIcon, items: ['Curriculum', 'Policies and governance', 'Reporting pathways and safeguarding practice']},
];

const getIconForStepName = (name) => {
    const lower = (name || '').toLowerCase();
    if (lower.includes('child')) return PersonOutlineIcon;
    if (lower.includes('parent')) return PeopleIcon;
    if (lower.includes('teacher')) return SchoolIcon;
    if (lower.includes('school')) return AccountBalanceIcon;
    if (lower.includes('compan') || lower.includes('privat') || lower.includes('business')) return BusinessIcon;
    if (lower.includes('gov') || lower.includes('public')) return GavelIcon;
    return VerifiedUserIcon;
};

export const AssessmentSuiteSection = () => {
    const { dashboardData } = useDashboard({ card_sections: 'S_2' });
    
    const sectionData = dashboardData?.cards?.sections?.find(
        (s) => s.section_code === 'S_2' || s.section_code === 'S2'
    );

    const titleCard = sectionData?.cards?.find(c => c.card_code === 'C_1');
    const stepCards = sectionData?.cards?.filter(c => c.card_type === 'StepCard') || [];
    const noteCard = sectionData?.cards?.find(c => c.card_type === 'LeftLineCard');

    const displayTitle = titleCard?.title || "Child Online Safety (COS) Assessments";
    const displayDescription = titleCard?.footer || "The COS Assessment Suite includes four ready-to-deploy assessment packs that help Governments establish a national child online safety baseline, identify priority gaps, and benchmark performance against global reference points.";
    
    const displaySteps = stepCards.length > 0 ? stepCards.map(c => ({
        num: String(c.step_num),
        title: c.step_name,
        icon: getIconForStepName(c.step_name),
        html: c.step_html,
        stepImg: c.step_img,
        items: []
    })) : SUITE_STEPS;

    const displayNote = noteCard?.leftline_html 
        ? noteCard.leftline_html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').replace(/\u000b/g, '').trim()
        : "Each group receives role-specific assessments, scorecards, and dashboard views to support targeted action.";

    return (
        <Box>
            <SectionHeader title={displayTitle} subtitle={displayDescription}/>

            <Box sx={{
                border: '1.5px solid #FCA5A5', borderRadius: '16px', p: {xs: 2.5, md: 4},
                backgroundColor: 'var(--brand-surface-subtle, #FAFAFA)', boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
            }}>
                <Grid container spacing={3.5} justifyContent="center">
                    {displaySteps.map((s) => {
                        const Icon = s.icon;
                        return (
                            <Grid item xs={12} sm={6} md={4} key={s.num}>
                                <Box sx={{
                                    border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.03)', p: 3, height: '100%',
                                    display: 'flex', flexDirection: 'column',
                                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                    '&:hover': {transform: 'translateY(-3px)', boxShadow: '0 8px 24px rgba(0,0,0,0.07)'},
                                }}>
                                    <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start', mb: 2}}>
                                        <Box sx={{
                                            width: 28, height: 28, borderRadius: '50%', backgroundColor: '#E8730C',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            color: '#fff', fontSize: '14px', fontWeight: 'bold', flexShrink: 0, mt: 0.2,
                                        }}>
                                            {s.num}
                                        </Box>
                                        <Box sx={{flex: 1}}>
                                            <Typography sx={{fontSize: '18px', fontWeight: 800, color: NAVY, mb: 1.5}}>
                                                {s.title}
                                            </Typography>
                                            {s.stepImg ? (
                                                <Box
                                                    component="img"
                                                    src={s.stepImg}
                                                    alt={s.title}
                                                    sx={{
                                                        height: 70,
                                                        maxWidth: '100%',
                                                        objectFit: 'contain',
                                                        mb: 2,
                                                        display: 'block',
                                                        // Line-art SVG icons are dark on transparent — invisible on the
                                                        // dark card. Lighten them in dark mode only (hue preserved).
                                                        'html[data-color-mode="dark"] &': { filter: 'invert(1) hue-rotate(180deg)' },
                                                    }}
                                                />
                                            ) : (
                                                <Icon style={{fontSize: 40, color: '#9CA3AF'}}/>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{
                                        flex: 1, 
                                        borderTop: '1px solid #F3F4F6', 
                                        pt: 2,
                                        '& ul': { m: 0, pl: 2.5 },
                                        '& li': { fontSize: '13.5px', color: '#4B5563', lineHeight: 1.6 },
                                        // Injected <li> is raw HTML, not MUI Typography, so the global
                                        // dark-mode rule can't reach it — make it legible in dark mode.
                                        'html[data-color-mode="dark"] & li': { color: 'var(--brand-text, #E5E7EB)' }
                                    }}>
                                        {s.html ? (
                                            <div dangerouslySetInnerHTML={{ __html: s.html }} />
                                        ) : (
                                            <Box component="ul" sx={{m: 0, pl: 2.5}}>
                                                {s.items.map((it) => (
                                                    <Box component="li" key={it} sx={{mb: 0.7}}>
                                                        <Typography sx={{fontSize: '13.5px', color: '#4B5563', lineHeight: 1.6}}>
                                                            {it}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                <Typography sx={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#1E3A8A',
                    textAlign: 'center',
                    mt: 4,
                    mb: 1
                }}>
                    {displayNote}
                </Typography>
            </Box>
        </Box>
    );
};

/* ══════════════════════════════════════════════════════════════════
   Slide 3 — What Does the COS Children Assessment Measure?
   ═════════════════════════════════════════════════════════════════ */
const parseHeadlineHtml = (html) => {
    if (!html) return null;
    let clean = html.replace(/^<p>/, '').replace(/<\/p>$/, '');
    const parts = clean.split(/<br\s*\/?>\s*<br\s*\/?>/i);
    let questionHtml = parts[0] || '';
    let badgeHtml = parts[1] || '';
    const badgeText = badgeHtml.replace(/<[^>]+>/g, '').trim();
    return {
        questionHtml,
        badgeText
    };
};

// "Are students prepared? Do they have…" -> bold lead question on its own line,
// then the rest — same as the Partner dashboard's card (Ian-PD-29Sep-1).
const splitLeadQuestion = (text) => {
    const clean = String(text || '').replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
    const idx = clean.indexOf('?');
    if (idx === -1) return {lead: '', rest: clean};
    return {lead: clean.slice(0, idx + 1), rest: clean.slice(idx + 1).trim()};
};

const MeasureCard = ({letter, color, title, question, badge, htmlContent, children}) => {
    const parsed = parseHeadlineHtml(htmlContent);
    const {lead, rest} = splitLeadQuestion(parsed ? parsed.questionHtml : question);
    const displayBadge = parsed ? parsed.badgeText : badge;

    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
            display: 'flex', flexDirection: 'column',
        }}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5}}>
                <Box sx={{
                    width: 42, height: 42, borderRadius: '50%', backgroundColor: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Typography sx={{fontSize: '18px', fontWeight: 800, color: '#fff'}}>{letter}</Typography>
                </Box>
                <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{title}</Typography>
            </Box>
            <Box sx={{mb: 1.5, minHeight: '72px'}}>
                {lead && <Typography sx={{fontSize: '13.5px', fontWeight: 700, color: NAVY, lineHeight: 1.5}}>{lead}</Typography>}
                {rest && <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.6}}>{rest}</Typography>}
            </Box>
            {displayBadge && (
                <Box sx={{
                    alignSelf: 'flex-start', backgroundColor: `${color}15`, color,
                    fontSize: '12px', fontWeight: 700, px: 1.5, py: 0.5, borderRadius: '6px', mb: 2,
                }}>
                    {displayBadge}
                </Box>
            )}
            <Box sx={{borderTop: '1px solid #F3F4F6', pt: 2, flex: 1}}>{children}</Box>
        </Box>
    );
};

// Fallback list content — reproduced verbatim from the Dashboard Common
// Section deck (slide 5), shared with every other dashboard's identical
// "About COS Children Assessment" page. Used only when the backend hasn't
// supplied list_objects for these cards yet.
const FALLBACK_READINESS_AREAS = [
    'Youth AI-Digital Activities',
    'Self-Regulated AI Digital Engagement',
    'Digital Hygiene and Privacy Management',
    'Cyberbullying Management',
    'Content Risk Avoidance',
    'Contact Risk Avoidance',
    'Authentic and Ethical Digital Identity',
    'Critical Information Management',
].map((name, idx) => ({code: `readiness-${idx}`, name, num: idx + 1}));

const FALLBACK_CONTEXT_AREAS = [
    'Digital Access at Home and School',
    'Youth Perception of Home and Support Environment',
    'Youth Perception of Parental Mediation',
    'Youth Perception of School AI-Digital Literacy Education',
].map((name, idx) => ({code: `context-${idx}`, name, num: idx + 1}));

const FALLBACK_CYBER_RISK_GROUPS = [
    {isMain: true, items: [{code: 'risk-1', name: 'Any Cyber Risk'}]},
    {category: 'CONDUCT RISK', items: [
        {code: 'risk-2', name: 'Cyberbullying'},
        {code: 'risk-3', name: 'Cybervictimization'},
        {code: 'risk-4', name: 'AI Image and Voice Misuse Victimization'},
    ]},
    {category: 'CONTENT RISK', items: [
        {code: 'risk-5', name: 'Sexual Content Exposure'},
        {code: 'risk-6', name: 'Sexual Contact Exposure'},
        {code: 'risk-7', name: 'AI Misinformation and Deceptive Content Risk'},
    ]},
    {category: 'CONTACT RISK', items: [
        {code: 'risk-8', name: 'Offline meetings with Online Strangers'},
        {code: 'risk-9', name: 'Sexual Conversation'},
        {code: 'risk-10', name: 'Unwanted Online Sexual Contact'},
        {code: 'risk-11', name: 'AI-Generated Sexual Image Risk'},
    ]},
    {category: 'CONTRACT & DATA RISK', items: [
        {code: 'risk-12', name: 'Misuse of Personal Information'},
        {code: 'risk-13', name: 'Underage Technology Use'},
    ]},
    {category: 'WELLBEING & SCREEN USE RISK', items: [
        {code: 'risk-14', name: 'Excessive Screen Time'},
        {code: 'risk-15', name: 'Video Game Addiction Risk'},
        {code: 'risk-16', name: 'Online Technology Addiction Risk'},
        {code: 'risk-17', name: 'AI Emotional Reliance'},
    ]},
];

const FALLBACK_ASSESSMENT_FOOTNOTE = (
    <>
        <p>* The risk definitions used in this assessment are consistent with the <strong>CPC Index global framework</strong>.</p>
        <p>The rubric tiers are based on each country’s percentile position within the 2017–2025 global reference distribution. National and global benchmark scores are aligned with the CPC Index methodology.</p>
        <p><strong>Any Cyber Risk</strong> refers to the proportion of children who experienced at least one of the cyber risks measured in the 2025 CPC Index, including content, contact, conduct, and contract risks including (Risks 2, 3, 5, 6, 8, 9, 10, 12, 13, 14, 15, 16).<br/>
        <strong>AI-related risks</strong> (Risks 4, 7, 11, 17) have been newly introduced in this assessment toolkit. Because sufficient historical and internationally comparable data are not yet available, national and global benchmark scores are not currently provided for these indicators.</p>
        <p><strong>CPC Index global benchmark:</strong> <a href="https://cpc.gcforum.org/global_cpc" target="_blank" rel="noreferrer">https://cpc.gcforum.org/global_cpc</a></p>
    </>
);

export const WhatItMeasuresSection = () => {
    const { dashboardData } = useDashboard({ card_sections: 'S_2,S_2a' });
    
    const sectionData = dashboardData?.cards?.sections?.find(
        (s) => s.section_code === 'S_2a' || s.section_code === 'S2a'
    );

    const sectionData2 = dashboardData?.cards?.sections?.find(
        (s) => s.section_code === 'S_2' || s.section_code === 'S2'
    );

    const titleCard = sectionData?.cards?.find(c => c.card_code === 'C_1');
    const headlineA = sectionData?.cards?.find(c => c.card_code === 'C_2');
    const listA     = sectionData?.cards?.find(c => c.card_code === 'C_3');
    const headlineB = sectionData?.cards?.find(c => c.card_code === 'C_4');
    const listB     = sectionData?.cards?.find(c => c.card_code === 'C_5');
    const headlineC = sectionData?.cards?.find(c => c.card_code === 'C_6');
    const listC     = sectionData?.cards?.find(c => c.card_code === 'C_7');

    // C_9 — methodology/benchmark footnote card, lives under S_2 (not S_2a like A/B/C above).
    const headlineD2 = sectionData2?.cards?.find(c => c.card_code === 'C_9');

    const dynamicReadinessAreas = Array.isArray(listA?.list_objects) && listA.list_objects.length > 0
        ? listA.list_objects.map(item => {
            const numMatch = typeof item.subname === 'string' ? item.subname.match(/\d+/) : null;
            const num = numMatch ? parseInt(numMatch[0], 10) : null;
            return {
                code: item.code,
                name: item.name,
                num
            };
        }).sort((a, b) => {
            if (a.num !== null && b.num !== null) return a.num - b.num;
            return 0;
        })
        : FALLBACK_READINESS_AREAS;

    const rawListB = listB?.list_objects;
    const cyberRiskGroups = Array.isArray(rawListB) && rawListB.length > 0
        ? (() => {
            const groupMap = new Map();
            rawListB.forEach(item => {
                const parentKey = item.parent_name || item.parent_code || 'Headline';
                const areaNumMatch = typeof item.parent_subname === 'string' ? item.parent_subname.match(/\d+/) : null;
                const areaNum = areaNumMatch ? parseInt(areaNumMatch[0], 10) : 99;

                const itemNumMatch = typeof item.subname === 'string' ? item.subname.match(/\d+/) : null;
                const itemNum = itemNumMatch ? parseInt(itemNumMatch[0], 10) : null;

                if (!groupMap.has(parentKey)) {
                    groupMap.set(parentKey, {
                        parent_code: item.parent_code,
                        parent_name: item.parent_name,
                        areaNum,
                        isMain: parentKey.toLowerCase() === 'headline' || item.parent_code?.toLowerCase() === 'headline',
                        category: (item.parent_name || '').toUpperCase(),
                        items: []
                    });
                }
                groupMap.get(parentKey).items.push({
                    code: item.code,
                    name: item.name,
                    itemNum
                });
            });

            const groups = Array.from(groupMap.values()).sort((a, b) => a.areaNum - b.areaNum);
            groups.forEach(g => {
                g.items.sort((a, b) => {
                    if (a.itemNum !== null && b.itemNum !== null) return a.itemNum - b.itemNum;
                    return 0;
                });
            });
            return groups;
        })()
        : FALLBACK_CYBER_RISK_GROUPS;

    const dynamicContextAreas = Array.isArray(listC?.list_objects) && listC.list_objects.length > 0
        ? listC.list_objects.map(item => {
            const numMatch = typeof item.subname === 'string' ? item.subname.match(/\d+/) : null;
            const num = numMatch ? parseInt(numMatch[0], 10) : null;
            return {
                code: item.code,
                name: item.name,
                num
            };
        }).sort((a, b) => {
            if (a.num !== null && b.num !== null) return a.num - b.num;
            return 0;
        })
        : FALLBACK_CONTEXT_AREAS;

    const displayTitle = titleCard?.title || "What Does the COS Children Assessment Measure?";
    const displaySubtitle = titleCard?.footer || "What do we need to understand to better support students in staying safe in the digital world?";

    return (
        <Box>
            <SectionHeader
                title={displayTitle}
                subtitle={displaySubtitle}
            />
            <Grid container spacing={3} alignItems="stretch">
                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="A" color={GREEN}
                        title={headlineA?.headline_title || "Child Online Safety Readiness"}
                        question="Are students prepared? Do they have the readiness to participate safely and responsibly in today’s AI-powered digital world?"
                        badge="8 Areas"
                        htmlContent={headlineA?.headline_html}
                    >
                        <Box component="ol" sx={{ m: 0, pl: 2.2 }}>
                            {dynamicReadinessAreas.map((item, idx) => (
                                <Box component="li" key={item.code || idx} value={item.num || undefined} sx={{ mb: 0.7, '&::marker': { color: GREEN, fontSize: '12.5px' } }}>
                                    <Typography sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5 }}>
                                        {item.name}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </MeasureCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="B" color={ORANGE}
                        title={headlineB?.headline_title || "Child Cyber-Risk Experiences *"}
                        question="What risks are students facing? How often do they report experiencing online risks in the past year?"
                        badge="17 Child Cyber-Risk Measures"
                        htmlContent={headlineB?.headline_html}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, pl: 2.2 }}>
                            {(() => {
                                let itemCounter = 0;
                                return cyberRiskGroups.map((group, idx) => {
                                    if (group.isMain) {
                                        const itemsToMap = group.items || [{ name: group.name }];
                                        return itemsToMap.map((item, itemIdx) => {
                                            itemCounter++;
                                            const itemName = typeof item === 'string' ? item : item.name;
                                            return (
                                                <Typography key={item.code || itemIdx} sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.6 }}>
                                                    {itemCounter}. {itemName}
                                                </Typography>
                                            );
                                        });
                                    }
                                    return (
                                        <Box key={idx} sx={{ mb: 0.5 }}>
                                            <Typography sx={{ 
                                                fontSize: '10.5px', 
                                                fontWeight: 800, 
                                                letterSpacing: '0.5px', 
                                                textTransform: 'uppercase',
                                                color: ORANGE, 
                                                mt: 1, 
                                                mb: 0.4 
                                            }}>
                                                {group.category}
                                            </Typography>
                                            {group.items.map((item, itemIdx) => {
                                                itemCounter++;
                                                const itemName = typeof item === 'string' ? item : item.name;
                                                return (
                                                    <Typography key={item.code || itemIdx} sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.6 }}>
                                                        {itemCounter}. {itemName}
                                                    </Typography>
                                                );
                                            })}
                                        </Box>
                                    );
                                });
                            })()}
                        </Box>
                    </MeasureCard>
                </Grid>
                <Grid item xs={12} md={4}>
                    <MeasureCard
                        letter="C" color={BLUE}
                        title={headlineC?.headline_title || "Context & Support"}
                        question="What supports do they have? Do families and schools help students stay safe?"
                        badge="4 Areas"
                        htmlContent={headlineC?.headline_html}
                    >
                        <Box component="ol" sx={{ m: 0, pl: 2.2 }}>
                            {dynamicContextAreas.map((item, idx) => (
                                <Box component="li" key={item.code || idx} value={item.num || undefined} sx={{ mb: 0.7, '&::marker': { color: BLUE, fontSize: '12.5px' } }}>
                                    <Typography sx={{ fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5 }}>
                                        {item.name}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </MeasureCard>
                </Grid>
            </Grid>

            <Box sx={{
                mt: 3, border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3,
                fontSize: '13px', color: '#374151', lineHeight: 1.6, '& p': {m: 0, mb: 1, '&:last-child': {mb: 0}},
            }}>
                {headlineD2?.headline_html ? (
                    <Box dangerouslySetInnerHTML={{__html: headlineD2.headline_html}}/>
                ) : FALLBACK_ASSESSMENT_FOOTNOTE}
            </Box>
        </Box>
    );
};

/* ══════════════════════════════════════════════════════════════════
   Slide 4 — COS Children Assessment Rubric Guide
   ═════════════════════════════════════════════════════════════════ */
const RUBRIC_GROUPS = [
    {
        icon: VerifiedUserIcon, color: GREEN,
        title: 'Child Online Safety Readiness',
        desc: 'Preparedness to act safely, responsibly, and effectively online.',
        bands: [
            {name: 'High',     color: READINESS_COLORS.High,     meaning: 'Strong, consistent readiness to participate online safely, responsibly, and effectively.'},
            {name: 'Moderate', color: READINESS_COLORS.Moderate, meaning: 'Generally adequate readiness, with some areas for reinforcement.'},
            {name: 'Basic',    color: READINESS_COLORS.Basic,    meaning: 'Partial or inconsistent readiness; targeted support may be needed.'},
            {name: 'Low',      color: READINESS_COLORS.Low,      meaning: 'Limited readiness or risky gaps requiring priority attention.'},
        ],
    },
    {
        icon: ReportProblemIcon, color: ORANGE,
        title: 'Child Cyber-Risk Experiences *',
        desc: 'Reported exposure to, involvement in, or impact from cyber risks.',
        bands: [
            {name: 'Monitor',          color: RISK_COLORS[0], meaning: 'Lower than the reference threshold or minimal cyber-risk signal'},
            {name: 'Watch',            color: RISK_COLORS[1], meaning: 'Noticeable signal or within expected reference range'},
            {name: 'Elevated',         color: RISK_COLORS[2], meaning: 'Above typical reference level or meaningful cyber-risk pattern'},
            {name: 'Priority Concern', color: RISK_COLORS[3], meaning: 'Top-quartile reference level or high cyber-risk prevalence'},
        ],
    },
    {
        icon: PeopleIcon, color: BLUE,
        title: 'Context & Support',
        desc: 'Access, guidance, school education, home support, and help-seeking conditions.',
        bands: [
            {name: 'Strong Enabling Context',   color: CONTEXT_COLORS[0], meaning: 'Strong access, support, guidance, or protective conditions.'},
            {name: 'Adequate Enabling Context', color: CONTEXT_COLORS[1], meaning: 'Generally positive conditions, with some minor limitations.'},
            {name: 'Limited Enabling Context',  color: CONTEXT_COLORS[2], meaning: 'Partial, inconsistent, or uncertain support conditions.'},
            {name: 'Low Enabling Context',      color: CONTEXT_COLORS[3], meaning: 'Weak, insufficient, or absent support conditions.'},
        ],
    },
    {
        icon: StorageIcon, color: '#4B5563',
        title: 'Participation and Data Confidence',
        desc: '',
        bands: [
            {name: 'High',     color: CONFIDENCE_COLORS[0], meaning: 'Strong enough for interpretation'},
            {name: 'Medium',   color: CONFIDENCE_COLORS[1], meaning: 'Usable with confidence label'},
            {name: 'Low',      color: CONFIDENCE_COLORS[2], meaning: 'Interpret cautiously'},
            {name: 'Very Low', color: CONFIDENCE_COLORS[3], meaning: 'Do not use for strong action or comparison'},
        ],
    },
];

const RubricCard = ({group}) => {
    const Icon = group.icon;
    return (
        <Box sx={{
            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
        }}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, mb: 1}}>
                <Box sx={{
                    width: 40, height: 40, borderRadius: '50%', backgroundColor: `${group.color}18`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                    <Icon style={{color: group.color, fontSize: 22}}/>
                </Box>
                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, lineHeight: 1.3}}>{group.title}</Typography>
            </Box>
            {group.desc && (
                <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6, mb: 2}}>{group.desc}</Typography>
            )}

            <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5}}>
                {group.bands.map((b) => (
                    <Box key={b.name}>
                        <Box sx={{display: 'inline-flex', mb: 0.6}}>
                            <Chip text={b.name} color={b.color}/>
                        </Box>
                        <Typography sx={{fontSize: '12.5px', color: '#4B5563', lineHeight: 1.5}}>{b.meaning}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
};

export const RubricGuideSection = () => (
    <Box>
        <SectionHeader
            title="COS Children Assessment Rubric Guide"
            subtitle="What was measured, and how should it be interpreted?"
        />
        <Grid container spacing={3} alignItems="stretch">
            {RUBRIC_GROUPS.map((g) => (
                <Grid item xs={12} sm={6} md={3} key={g.title}>
                    <RubricCard group={g}/>
                </Grid>
            ))}
        </Grid>
        <NoteBanner
            label="Decision guidance:"
            text={[
                'Readiness scores describe student preparedness.',
                'Risk scores describe reported risk experience.',
                'Context scores describe conditions around the child, not the child’s competency.',
            ]}
        />
        <Typography sx={{fontSize: '12px', color: '#9CA3AF', fontStyle: 'italic', mt: 2, lineHeight: 1.6}}>
            * For established risks, bands use risk-specific reference thresholds. For AI-related risks, provisional
            thresholds are used until enough reference data exists.
        </Typography>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   How COS Readiness Solution Scores Are Structured — static, no
   backend section yet, same as School/Network/Partner's own version.
   ═════════════════════════════════════════════════════════════════ */
const READINESS_SOLUTION_STEPS = [
    {num: '1', title: 'COS Baseline Score', body: 'Defines initial COS readiness level and identifies priority areas for improvement.'},
    {num: '2', title: 'Learning Impact Signal', body: 'Measures learning program impact using pre- and post-assessment evidence to show students’ progress in targeted COS areas.'},
    {num: '3', title: 'COS Reassessment Score', body: 'Confirms how much COS readiness has improved since the baseline assessment.'},
];

export const ReadinessSolutionScoresSection = () => (
    <Box>
        <SectionHeader title="How Child Online Safety (COS) Readiness Solution Scores Are Structured"/>

        <Grid container spacing={2.5} alignItems="stretch">
            {READINESS_SOLUTION_STEPS.map((s, i) => (
                <Grid item xs={12} sm={6} md={4} key={s.num}>
                    <Box sx={{
                        position: 'relative', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                        backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                    }}>
                        <Box sx={{
                            width: 34, height: 34, borderRadius: '50%', backgroundColor: GREEN,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                        }}>
                            <Typography sx={{fontSize: '16px', fontWeight: 800, color: '#fff'}}>{s.num}</Typography>
                        </Box>
                        <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5}}>{s.title}</Typography>
                        <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{s.body}</Typography>
                        {i < READINESS_SOLUTION_STEPS.length - 1 && (
                            <Typography sx={{
                                display: {xs: 'none', md: 'block'}, position: 'absolute', right: -14, top: '50%',
                                transform: 'translateY(-50%)', color: '#CBD5E1', fontSize: '22px', fontWeight: 800, zIndex: 1,
                            }}>
                                ›
                            </Typography>
                        )}
                    </Box>
                </Grid>
            ))}
        </Grid>
    </Box>
);

/* ══════════════════════════════════════════════════════════════════
   Slide 6 — How COS Children Assessment Scores Are Structured
   ═════════════════════════════════════════════════════════════════ */
const SCORE_STEPS = [
    {num: '1', title: 'Questions',        body: 'What students answer in the survey.'},
    {num: '2', title: 'Item Scores',      body: 'Related answers are summarized into diagnostic topics.'},
    {num: '3', title: 'Area Scores',      body: 'Items are grouped into broader readiness, risk, and context areas.'},
    {num: '4', title: 'Composite Scores', body: 'Results show strengths, risks, and action priorities.'},
];
const SCORE_STATS = [
    {value: '179', label: 'Questions', body: 'Survey questions covering readiness, risks, and environment.'},
    {value: '37',  label: 'Items',     body: 'Diagnostic topics that combine related responses.'},
    {value: '12',  label: 'Areas',     body: 'Broader groups used to summarize child online safety readiness, risks, and context and supports.'},
];

export const ScoreStructureSection = () => {
    const { dashboardData } = useDashboard({ card_sections: 'S_2b' });
    
    const sectionData = dashboardData?.cards?.sections?.find(
        (s) => s.section_code === 'S_2b' || s.section_code === 'S2b'
    );

    const titleCard = sectionData?.cards?.find(c => c.card_code === 'C_1');
    const stepCards = sectionData?.cards?.filter(c => c.card_type === 'StepCard') || [];
    const headlineCard = sectionData?.cards?.find(c => c.card_type === 'HeadlineCard');

    const displayTitle = titleCard?.title || "How COS Children Assessment Scores Are Structured";
    const displayDescription = titleCard?.footer || "COS Children Assessment scores are built step by step. Survey answers are summarized into item, area, and composite scores to help you identify strengths, risks, and action priorities.";

    const scoreStepCards = stepCards.filter(c => ['C_2', 'C_3', 'C_4', 'C_5'].includes(c.card_code));
    const displaySteps = scoreStepCards.length > 0 ? scoreStepCards.map(c => ({
        num: String(c.step_num),
        title: c.step_name,
        body: c.step_html ? c.step_html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').replace(/\u000b/g, '').trim() : ''
    })) : SCORE_STEPS;

    const displayHeadline = headlineCard?.headline_title || "Structures Used in the COS Children Assessment";

    const scoreStatCards = stepCards.filter(c => ['C_7', 'C_8', 'C_9'].includes(c.card_code));
    const displayStats = scoreStatCards.length > 0 ? scoreStatCards.map(c => {
        const match = c.step_name.match(/^(\d+)\s+(.*)$/);
        const value = match ? match[1] : '';
        const label = match ? match[2] : c.step_name;
        return {
            value,
            label,
            body: c.step_html ? c.step_html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').replace(/\u000b/g, '').trim() : ''
        };
    }) : SCORE_STATS;

    return (
        <Box>
            <SectionHeader title={displayTitle} subtitle={displayDescription}/>

            <Grid container spacing={2.5} alignItems="stretch">
                {displaySteps.map((s, i) => (
                    <Grid item xs={12} sm={6} md={3} key={s.num}>
                        <Box sx={{
                            position: 'relative', border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px',
                            backgroundColor: 'var(--brand-surface, #ffffff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                        }}>
                            <Box sx={{
                                width: 34, height: 34, borderRadius: '50%', backgroundColor: GREEN,
                                display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
                            }}>
                                <Typography sx={{fontSize: '16px', fontWeight: 800, color: '#fff'}}>{s.num}</Typography>
                            </Box>
                            <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY, mb: 0.5}}>{s.title}</Typography>
                            <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{s.body}</Typography>
                            {i < displaySteps.length - 1 && (
                                <Typography sx={{
                                    display: {xs: 'none', md: 'block'}, position: 'absolute', right: -14, top: '50%',
                                    transform: 'translateY(-50%)', color: '#CBD5E1', fontSize: '22px', fontWeight: 800, zIndex: 1,
                                }}>
                                    ›
                                </Typography>
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>

            <Typography sx={{fontSize: '13px', fontWeight: 700, color: NAVY, mt: 4, mb: 2}}>
                {displayHeadline}
            </Typography>
            <Grid container spacing={2.5} alignItems="stretch">
                {displayStats.map((st) => (
                    <Grid item xs={12} sm={4} key={st.label}>
                        <Box sx={{
                            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3, height: '100%',
                        }}>
                            <Box sx={{display: 'flex', alignItems: 'baseline', gap: 1, mb: 1}}>
                                <Typography sx={{fontSize: '34px', fontWeight: 800, color: TIER_COLOR.Exceptional, lineHeight: 1}}>
                                    {st.value}
                                </Typography>
                                <Typography sx={{fontSize: '15px', fontWeight: 700, color: NAVY}}>{st.label}</Typography>
                            </Box>
                            <Typography sx={{fontSize: '13px', color: '#6B7280', lineHeight: 1.6}}>{st.body}</Typography>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

/* ══════════════════════════════════════════════════════════════════
   Slide 7 — What This Report Tells You (National)
   ═════════════════════════════════════════════════════════════════ */
const REPORT_CARDS = [
    {
        barColor: '#1B6B55', label: 'This report helps you understand',
        items: [
            'How prepared children are to act safely and responsibly online.',
            'Which cyber risks children report experiencing.',
            'Which regions, schools or groups may need targeted support.',
            'Whether national data coverage is strong enough to guide policy.',
        ],
    },
    {
        barColor: '#3B6FA0', label: 'Use it to decide',
        items: [
            'Where national policy or implementation support is needed.',
            'Which risks require urgent safeguarding or prevention action.',
            'Which regions or schools need more assessment coverage.',
            'What to share with ministries, schools, partners or the public.',
        ],
    },
    {
        barColor: '#DC2626', label: 'Do not use it to',
        items: [
            'Label or blame children, schools or regions.',
            'Treat context factors as performance.',
            'Compare small groups or regions without sufficient data.',
            'Read cyber-risk prevalence as a competency score.',
        ],
    },
];

const DEFAULT_COLORS = ['#1B6B55', '#3B6FA0', '#DC2626'];

export const UsingReportSection = () => {
    const { dashboardData } = useDashboard({ card_sections: 'S_2c' });
    
    const sectionData = dashboardData?.cards?.sections?.find(
        (s) => s.section_code === 'S_2c' || s.section_code === 'S2c'
    );

    const titleCard = sectionData?.cards?.find(c => c.card_code === 'C_1');
    const displayTitle = titleCard?.title || "What This Report Tells You";
    const displaySubtitle = titleCard?.footer || "How should you use this report?";

    const leftLineCards = sectionData?.cards?.filter(c => c.card_type === 'LeftLineCard') || [];

    const displayCards = leftLineCards.length > 0 ? leftLineCards.map((c, index) => {
        const barColor = (c.accent_color && c.accent_color !== '#000000')
            ? c.accent_color
            : DEFAULT_COLORS[index % DEFAULT_COLORS.length];

        let label = '';
        let items = [];
        if (c.leftline_html) {
            const h1Match = c.leftline_html.match(/<h1>(.*?)<\/h1>/i);
            if (h1Match) {
                label = h1Match[1];
            }
            const liMatches = c.leftline_html.match(/<li>[\s\S]*?<\/li>/gi);
            if (liMatches) {
                items = liMatches.map(li => li.replace(/<\/?li>/gi, '').trim());
            }
        }

        return {
            barColor,
            label: label || c.card_code,
            items: items.length > 0 ? items : []
        };
    }) : REPORT_CARDS;

    return (
        <Box>
            <SectionHeader
                title={displayTitle}
                subtitle={displaySubtitle}
            />
            <Grid container spacing={3} alignItems="stretch" sx={{mt: 2}}>
                {displayCards.map((card, i) => (
                    <Grid item xs={12} md={4} key={i}>
                        <Box sx={{
                            border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #ffffff)',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflow: 'hidden', height: '100%',
                            minHeight: {md: '520px'}, display: 'flex', flexDirection: 'column',
                        }}>
                            <Box sx={{backgroundColor: card.barColor, height: '6px', flexShrink: 0}}/>
                            <Box sx={{p: {xs: 3, md: 4}, display: 'flex', flexDirection: 'column', flex: 1}}>
                                <Typography sx={{fontSize: '17px', fontWeight: 700, color: card.barColor, mb: 3, lineHeight: 1.3}}>
                                    {card.label}
                                </Typography>
                                <Box component="ul" sx={{m: 0, pl: 2.5, flex: 1, display: 'flex', flexDirection: 'column', gap: 2.5}}>
                                    {card.items.map((item) => (
                                        <Box component="li" key={item}>
                                            <Typography sx={{fontSize: '14.5px', color: '#374151', lineHeight: 1.65}} dangerouslySetInnerHTML={{ __html: item }} />
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};
